import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
  ORDER_FEATURES,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence, selectCompTypes } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectSubjectDepreciationRatioForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';

const calculateTotalBasementAreaAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatio: number | undefined,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;

  if (compType === 'arv') {
    subjectImprovements = improvements.subjectArv;
  }
  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(depreciationRatio) ||
    isUndefined(subjectPropertyIdentification)
  ) {
    return undefined;
  }

  const {
    basement,
    craftsmanQuality: quality,
    propertyType = 'singleFamilyResidence',
  } = subjectImprovements;
  const { basement: compBasement } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;

  if (isUndefined(quality) || (quality * 10) % 1 !== 0 || quality < 1 || isUndefined(zipCode)) {
    return undefined;
  }

  const totalArea = basement && ORDER_FEATURES.basement.hasFeature(subjectImprovements)
    ? AloftArea.schema.parse(basement?.totalArea)
    : AloftArea.zero;

  const compTotalArea = isDefined(compBasement?.totalArea)
    ? AloftArea.schema.parse(compBasement?.totalArea)
    : AloftArea.zero;

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  return adjCalculator.getTotalBasementAreaAdj({
    subject: {
      totalArea: AloftArea.schema.parse(totalArea),
      quality,
      propertyType,
      zipCode,
    },
    comp: { totalArea: AloftArea.schema.parse(compTotalArea) },
    depreciationRatio,
  }).adjustment;
};

export const selectTotalBasementAreaAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    (state, id: string) => id,
    selectCompTypes,
  ],
  calculateTotalBasementAreaAdj,
);

export const selectTotalBasementAreaAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectCompSequence,
    selectCompTypes,
  ],
  (adjustmentVersion, improvements, propertyIdentification, depreciationRatio, compSequence, compTypes) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateTotalBasementAreaAdj(
        adjustmentVersion,
        improvements,
        propertyIdentification,
        depreciationRatio,
        propID,
        compTypes,
      );
      if (isDefined(adj)) acc[propID] = adj;
      return acc;
    }, {}),
);
